/**
 * Utility functions for map calculations
 * Used for dumpster rectangle positioning and rotation on Google Maps
 */

/**
 * Convert feet to meters
 */
export function feetToMeters(feet: number): number {
  return feet * 0.3048;
}

/**
 * Parse container dimension string (e.g., "14' L x 7.5' W x 4' H")
 * Returns dimensions in feet
 */
export function parseContainerDimensions(dimensionString: string): {
  length: number;
  width: number;
  height: number;
} | null {
  try {
    // Match patterns like "14' L x 7.5' W x 4' H" or "21'11" L x 7'0" W x 5'2" H"
    const pattern = /(\d+)(?:'(\d+)"?)?\s*L\s*x\s*(\d+)(?:'(\d+)"?)?\s*W\s*x\s*(\d+)(?:'(\d+)"?)?\s*H/i;
    const match = dimensionString.match(pattern);

    if (!match) {
      return null;
    }

    // Parse length (feet + inches)
    const lengthFeet = Number.parseInt(match[1], 10);
    const lengthInches = match[2] ? Number.parseInt(match[2], 10) : 0;
    const length = lengthFeet + lengthInches / 12;

    // Parse width (feet + inches)
    const widthFeet = Number.parseInt(match[3], 10);
    const widthInches = match[4] ? Number.parseInt(match[4], 10) : 0;
    const width = widthFeet + widthInches / 12;

    // Parse height (feet + inches)
    const heightFeet = Number.parseInt(match[5], 10);
    const heightInches = match[6] ? Number.parseInt(match[6], 10) : 0;
    const height = heightFeet + heightInches / 12;

    return { length, width, height };
  } catch {
    return null;
  }
}

/**
 * Calculate new lat/lng from a starting point with offsets in meters
 * @param point Starting lat/lng point
 * @param metersNorth Offset north in meters (positive = north, negative = south)
 * @param metersEast Offset east in meters (positive = east, negative = west)
 */
export function latLngOffset(
  point: { lat: number; lng: number },
  metersNorth: number,
  metersEast: number,
): { lat: number; lng: number } {
  // Earth's radius in meters
  const earthRadius = 6378137;

  // Calculate new latitude
  const newLat = point.lat + (metersNorth / earthRadius) * (180 / Math.PI);

  // Calculate new longitude (adjust for latitude)
  const newLng =
    point.lng +
    ((metersEast / earthRadius) * (180 / Math.PI)) / Math.cos((point.lat * Math.PI) / 180);

  return { lat: newLat, lng: newLng };
}

/**
 * Calculate the four corners of a rotated rectangle
 * @param center Center point (lat/lng)
 * @param lengthMeters Length of rectangle in meters (along rotation axis)
 * @param widthMeters Width of rectangle in meters (perpendicular to rotation)
 * @param rotation Rotation angle in degrees (0 = north)
 * @returns Array of 4 corner points in order: top-left, top-right, bottom-right, bottom-left
 */
export function calculateRectangleCorners(
  center: { lat: number; lng: number },
  lengthMeters: number,
  widthMeters: number,
  rotation: number,
): { lat: number; lng: number }[] {
  // Convert rotation to radians
  const rotationRad = (rotation * Math.PI) / 180;

  // Half dimensions
  const halfLength = lengthMeters / 2;
  const halfWidth = widthMeters / 2;

  // Calculate corner offsets (before rotation)
  // In a north-oriented rectangle (rotation = 0):
  // - Length runs north-south
  // - Width runs east-west
  const corners = [
    { north: halfLength, east: -halfWidth }, // Top-left
    { north: halfLength, east: halfWidth }, // Top-right
    { north: -halfLength, east: halfWidth }, // Bottom-right
    { north: -halfLength, east: -halfWidth }, // Bottom-left
  ];

  // Apply rotation and convert to lat/lng
  return corners.map((corner) => {
    // Rotate the offset
    const rotatedNorth =
      corner.north * Math.cos(rotationRad) - corner.east * Math.sin(rotationRad);
    const rotatedEast =
      corner.north * Math.sin(rotationRad) + corner.east * Math.cos(rotationRad);

    // Apply offset to center point
    return latLngOffset(center, rotatedNorth, rotatedEast);
  });
}

/**
 * Calculate the position for the door triangle (inverted triangle inside rectangle)
 * Returns the three points of the triangle
 * @param center Center point of rectangle (lat/lng)
 * @param lengthMeters Length of rectangle in meters
 * @param widthMeters Width of rectangle in meters
 * @param rotation Rotation angle in degrees (0 = north)
 * @returns Array of 3 points for the triangle: [apex, left-point, right-point]
 */
export function calculateDoorTriangle(
  center: { lat: number; lng: number },
  lengthMeters: number,
  widthMeters: number,
  rotation: number,
): { lat: number; lng: number }[] {
  // Convert rotation to radians
  const rotationRad = (rotation * Math.PI) / 180;

  // Triangle dimensions
  // Width should match the short end of rectangle (widthMeters)
  // Height is 15% of the length
  const triangleHeight = lengthMeters * 0.15; // 15% of length
  const triangleWidth = widthMeters; // Full width of rectangle's short end

  // Position triangle at the "top" end of rectangle (north when rotation = 0)
  // The apex points inward (toward center), base is at the edge
  const baseOffset = lengthMeters / 2; // At the top edge
  const apexOffset = baseOffset - triangleHeight; // Points inward

  // Triangle points (before rotation)
  const points = [
    { north: apexOffset, east: 0 }, // Apex (points into rectangle)
    { north: baseOffset, east: -triangleWidth / 2 }, // Left point of base
    { north: baseOffset, east: triangleWidth / 2 }, // Right point of base
  ];

  // Apply rotation and convert to lat/lng
  return points.map((point) => {
    // Rotate the offset
    const rotatedNorth = point.north * Math.cos(rotationRad) - point.east * Math.sin(rotationRad);
    const rotatedEast = point.north * Math.sin(rotationRad) + point.east * Math.cos(rotationRad);

    // Apply offset to center point
    return latLngOffset(center, rotatedNorth, rotatedEast);
  });
}
